// Kilimanjaro climbing routes and camps.
// Elevations are commonly-cited approximate figures used for orientation in the
// 3D visualization below — not surveyed GPS data. Angular positions are stylized
// to spread routes clearly around the mountain rather than exact compass bearings.

export interface KiliCamp {
  name: string;
  /** Approx elevation in meters */
  elevation: number;
  /** Short blurb shown in the marker popup */
  note?: string;
}

export interface KiliRoute {
  id: string;
  name: string;
  /** Hex color used for the route line + markers */
  color: string;
  /** Typical trip length in days */
  days: string;
  /** Degrees (0-360) where the route starts around the base of the mountain */
  angleStart: number;
  /** How many degrees the route sweeps around the mountain as it climbs */
  angleSpan: number;
  /** Short description */
  description: string;
  camps: KiliCamp[];
}

export const SUMMIT_ELEVATION = 5895;

export const KILIMANJARO_ROUTES: KiliRoute[] = [
  {
    id: 'machame',
    name: 'Machame Route',
    color: '#2dd4bf',
    days: '6-7 days',
    angleStart: 220,
    angleSpan: 110,
    description: 'The "Whiskey Route" — scenic and popular, approaching the summit from the south.',
    camps: [
      { name: 'Machame Gate', elevation: 1800, note: 'Trailhead through rainforest' },
      { name: 'Machame Camp', elevation: 3000, note: 'First night, forest edge' },
      { name: 'Shira Camp', elevation: 3840, note: 'Open moorland plateau' },
      { name: 'Barranco Camp', elevation: 3960, note: 'Below the Barranco Wall' },
      { name: 'Karanga Camp', elevation: 4035, note: 'Acclimatization stop' },
      { name: 'Barafu Camp', elevation: 4673, note: 'Base camp for summit night' },
      { name: 'Uhuru Peak', elevation: SUMMIT_ELEVATION, note: 'Summit — highest point in Africa' },
    ],
  },
  {
    id: 'marangu',
    name: 'Marangu Route',
    color: '#f59e0b',
    days: '5-6 days',
    angleStart: 90,
    angleSpan: 25,
    description: 'The "Coca-Cola Route" — the only route with hut accommodation, approaching from the east.',
    camps: [
      { name: 'Marangu Gate', elevation: 1870, note: 'Trailhead' },
      { name: 'Mandara Hut', elevation: 2700, note: 'Rainforest huts' },
      { name: 'Horombo Hut', elevation: 3720, note: 'Moorland huts, acclimatization stop' },
      { name: 'Kibo Hut', elevation: 4703, note: 'Base for summit night' },
      { name: 'Uhuru Peak', elevation: SUMMIT_ELEVATION, note: 'Summit — highest point in Africa' },
    ],
  },
  {
    id: 'lemosho',
    name: 'Lemosho Route',
    color: '#a78bfa',
    days: '7-8 days',
    angleStart: 255,
    angleSpan: 150,
    description: 'A remote, high-success-rate route approaching from the west before joining the southern circuit.',
    camps: [
      { name: 'Londorossi Gate', elevation: 2100, note: 'Trailhead' },
      { name: 'Mti Mkubwa', elevation: 2820, note: 'Forest Camp' },
      { name: 'Shira 1 Camp', elevation: 3505, note: 'Shira Plateau' },
      { name: 'Shira 2 Camp', elevation: 3850, note: 'Shira Plateau' },
      { name: 'Barranco Camp', elevation: 3960, note: 'Below the Barranco Wall' },
      { name: 'Karanga Camp', elevation: 4035, note: 'Acclimatization stop' },
      { name: 'Barafu Camp', elevation: 4673, note: 'Base camp for summit night' },
      { name: 'Uhuru Peak', elevation: SUMMIT_ELEVATION, note: 'Summit — highest point in Africa' },
    ],
  },
  {
    id: 'rongai',
    name: 'Rongai Route',
    color: '#60a5fa',
    days: '6-7 days',
    angleStart: 10,
    angleSpan: 55,
    description: 'The only northern route, approaching from near the Kenyan border — drier and quieter.',
    camps: [
      { name: 'Rongai Gate', elevation: 1900, note: 'Trailhead' },
      { name: 'Simba Camp', elevation: 2600, note: 'First Cave' },
      { name: 'Kikelewa Camp', elevation: 3679, note: 'Caves campsite' },
      { name: 'Mawenzi Tarn Camp', elevation: 4330, note: 'Beneath Mawenzi peak' },
      { name: 'Kibo Hut', elevation: 4703, note: 'Base for summit night' },
      { name: 'Uhuru Peak', elevation: SUMMIT_ELEVATION, note: 'Summit — highest point in Africa' },
    ],
  },
  {
    id: 'umbwe',
    name: 'Umbwe Route',
    color: '#fb7185',
    days: '5-6 days',
    angleStart: 195,
    angleSpan: 95,
    description: 'The steepest, most direct route — for experienced trekkers, joining the southern circuit at Barranco.',
    camps: [
      { name: 'Umbwe Gate', elevation: 1600, note: 'Trailhead through dense forest' },
      { name: 'Umbwe Cave Camp', elevation: 2940, note: 'Steep forest ridge' },
      { name: 'Barranco Camp', elevation: 3960, note: 'Below the Barranco Wall' },
      { name: 'Karanga Camp', elevation: 4035, note: 'Acclimatization stop' },
      { name: 'Barafu Camp', elevation: 4673, note: 'Base camp for summit night' },
      { name: 'Uhuru Peak', elevation: SUMMIT_ELEVATION, note: 'Summit — highest point in Africa' },
    ],
  },
  {
    id: 'northern-circuit',
    name: 'Northern Circuit',
    color: '#34d399',
    days: '8-9 days',
    angleStart: 255,
    angleSpan: 300,
    description: 'The longest route, circling the quiet northern slopes for the best acclimatization profile.',
    camps: [
      { name: 'Londorossi Gate', elevation: 2100, note: 'Trailhead' },
      { name: 'Mti Mkubwa', elevation: 2820, note: 'Forest Camp' },
      { name: 'Shira 1 Camp', elevation: 3505, note: 'Shira Plateau' },
      { name: 'Shira 2 Camp', elevation: 3850, note: 'Shira Plateau' },
      { name: 'Moir Hut', elevation: 4200, note: 'Quiet northern slopes' },
      { name: 'Buffalo Camp', elevation: 3960, note: 'Northern circuit' },
      { name: 'Third Cave Camp', elevation: 3900, note: 'Northern circuit' },
      { name: 'School Hut', elevation: 4800, note: 'Base for summit night' },
      { name: 'Uhuru Peak', elevation: SUMMIT_ELEVATION, note: 'Summit — highest point in Africa' },
    ],
  },
];
